import winkNLP from "wink-nlp";
import winkModel from "wink-eng-lite-web-model";
import winkBM25TextSearch from "wink-bm25-text-search";
import NLP from "../NLP";
const winkNlp = winkNLP(winkModel);
const allQueries = [];
const modelName = "TaylorAI/gte-tiny";
export default class HybridTextSearch {
    extraction;
    bm25TextSearch;
    initPromise;
    corpus;
    sentenceVectors = [];
    allTestQueries;
    prefixTopics = true;
    constructor(corpus) {
        this.corpus = corpus;
        // Set up BM25 text search
        // Step I: Define config
        const engine = new winkBM25TextSearch();
        engine.defineConfig({
            fldWeights: { body: 1, metadata: 2 },
            ovFldNames: ["docIndex"]
        });
        // Step II: Define PrepTasks pipe.
        const its = winkNlp.its;
        const prepTask = (text) => {
            const tokens = [];
            winkNlp.readDoc(text)
                .tokens()
                .filter((t) => (t.out(its.type) === "word" && !t.out(its.stopWordFlag)))
                .each((t) => tokens.push((t.out(its.negationFlag)) ? "!" + t.out(its.stem) : t.out(its.stem)));
            return tokens;
        };
        engine.definePrepTasks([prepTask]);
        // Step III: Add Docs
        corpus.forEach((entry, i) => {
            const docId = `${i}`;
            const doc = {
                docIndex: i,
                body: entry.sentences.join(" "),
                metadata: entry.metadata || ""
            };
            engine.addDoc(doc, docId);
        });
        // // Step IV: Consolidate
        engine.consolidate();
        this.bm25TextSearch = engine;
        // Set up transformer embedding pipeline
        const embedPromises = [];
        const pipelineInitPromise = this.getPipeline(modelName);
        pipelineInitPromise.then(extraction => {
            this.extraction = extraction;
            // Embed sentence vectors in corpus
            for (let i = 0; i < corpus.length; ++i) {
                const entry = corpus[i];
                for (let k = 0; k < entry.sentences.length; ++k) {
                    let sentence = entry.sentences[k];
                    if (this.prefixTopics) {
                        if (entry.sentenceTopics?.length > k && entry.sentenceTopics[k]) {
                            sentence = NLP.capitalizeFirstLetter(entry.sentenceTopics[k]) + ": " + sentence;
                        }
                    }
                    const promise = this.extraction(sentence, { pooling: "mean", normalize: true });
                    promise.then(result => {
                        this.sentenceVectors.push({
                            entryId: i,
                            sentenceId: k,
                            sentence: entry.sentences[k],
                            vector: result.data
                        });
                    });
                    embedPromises.push(promise);
                }
            }
        });
        // Set up test queries
        const unrelatedQueries = [
            "What's my daily agenda?",
            "How are you feeling?",
            "When should I get ready?",
            "What are your capabilities?",
            "What can you do?"
        ];
        for (let i = 0; i < corpus.length; ++i) {
            const testQueries = corpus[i].testQueries;
            for (const j in testQueries) {
                const queries = testQueries[j];
                queries.forEach(query => {
                    allQueries.push({
                        query: query,
                        entryId: i,
                        sentenceId: j
                    });
                });
            }
        }
        for (const unrelated of unrelatedQueries) {
            allQueries.push({ query: unrelated, entryId: -1, sentenceId: -1 });
        }
        this.initPromise = new Promise(resolve => {
            Promise.all([pipelineInitPromise, ...embedPromises]).then(() => {
                resolve();
            });
        });
    }
    async getPipeline(modelName) {
        const task = "feature-extraction";
        // On clients, this should be initialized using a module import in App.ts
        if (NLP.pipeline) {
            return NLP.pipeline(task, modelName);
        }
        // Dynamic import will be used in Node (CommonJS) environments, including Jest
        // See issue: https://github.com/xenova/transformers.js/issues/80
        const TransformersApi = Function("return import('@xenova/transformers')")();
        const { pipeline } = await TransformersApi;
        return pipeline(task, modelName);
    }
    async evalEmbeddings() {
        await this.initPromise;
        const evalResult = new EvalResult();
        evalResult.nTotalQueries = allQueries.length;
        const pQueries = [];
        for (const testQuery of allQueries) {
            const query = testQuery.query;
            const promise = this.extraction(query, { pooling: "mean", normalize: true });
            pQueries.push(promise);
            promise.then(result => {
                const qVector = result.data;
                const printResult = {
                    query: query,
                    results: [],
                    correctDoc: false,
                    correctSentence: false
                };
                for (const sentenceVector of this.sentenceVectors) {
                    const value = +cosineSimilarity(sentenceVector.vector, qVector);
                    printResult.results.push({
                        value: value,
                        sentenceVector: sentenceVector
                    });
                }
                printResult.results.sort((a, b) => {
                    return b.value - a.value;
                });
                const topResult = printResult.results[0];
                if (topResult.sentenceVector.entryId == testQuery.entryId) {
                    printResult.correctDoc = true;
                    if (topResult.sentenceVector.sentenceId == testQuery.sentenceId) {
                        printResult.correctSentence = true;
                    }
                }
                if (printResult.correctDoc)
                    evalResult.nDocsCorrect++;
                if (printResult.correctSentence)
                    evalResult.nSentencesCorrect++;
                console.log(printResult);
            });
        }
        await Promise.all(pQueries);
        printEvalResult(evalResult);
        return evalResult;
    }
    getCorpusEntry(entryId) {
        if (entryId > -1 && entryId < this.corpus.length) {
            return this.corpus[entryId];
        }
        return undefined;
    }
    async query(query) {
        await this.initPromise;
        const pQueries = [];
        const queryResult = new QueryResult();
        const docResults = this.bm25TextSearch.search(query, this.corpus.length);
        const printResult = {
            query: query,
            nDocs: docResults.length,
            docResults: docResults,
            results: [],
        };
        if (docResults.length > 0) {
            const topDocIndex = docResults[0][0];
            queryResult.entryId = topDocIndex;
            const promise = this.extraction(query, { pooling: "mean", normalize: true });
            pQueries.push(promise);
            promise.then(result => {
                const qVector = result.data;
                const svs = this.sentenceVectors.filter(sv => sv.entryId == topDocIndex);
                for (const sentenceVector of svs) {
                    const value = +cosineSimilarity(sentenceVector.vector, qVector);
                    printResult.results.push({
                        value: value,
                        sentenceVector: sentenceVector
                    });
                }
                printResult.results.sort((a, b) => {
                    return b.value - a.value;
                });
                const topResult = printResult.results[0];
                queryResult.sentenceId = topResult.sentenceVector.sentenceId;
                queryResult.sentence = topResult.sentenceVector.sentence;
                queryResult.similarityValue = topResult.value;
            });
        }
        else {
            queryResult.entryId = -1;
            queryResult.sentenceId = -1;
        }
        await Promise.all(pQueries);
        return queryResult;
    }
    async evalHybrid() {
        await this.initPromise;
        const pQueries = [];
        const printResults = [];
        for (const testQuery of allQueries) {
            const query = testQuery.query;
            // Search document first with BM25
            const docResults = this.bm25TextSearch.search(query, this.corpus.length);
            const printResult = {
                query: query,
                nDocs: docResults.length,
                docResults: docResults,
                results: [],
                correctDoc: false,
                correctSentence: false
            };
            printResults.push(printResult);
            if (docResults.length > 0) {
                const topDocIndex = docResults[0][0];
                if (topDocIndex == testQuery.entryId) {
                    printResult.correctDoc = true;
                }
                const promise = this.extraction(query, { pooling: "mean", normalize: true });
                pQueries.push(promise);
                promise.then(result => {
                    // Compare embeddings for only sentences from that doc
                    const qVector = result.data;
                    const svsToUse = this.sentenceVectors;
                    const svs = svsToUse.filter(sv => sv.entryId == topDocIndex);
                    for (const sentenceVector of svs) {
                        const value = +cosineSimilarity(sentenceVector.vector, qVector);
                        printResult.results.push({
                            value: value,
                            sentenceVector: sentenceVector
                        });
                    }
                    printResult.results.sort((a, b) => {
                        return b.value - a.value;
                    });
                    const topResult = printResult.results[0];
                    if (topResult.sentenceVector.sentenceId == testQuery.sentenceId) {
                        printResult.correctSentence = true;
                    }
                    console.log(printResult);
                });
            }
            else {
                if (testQuery.entryId == -1) {
                    printResult.correctDoc = true;
                    printResult.correctSentence = true;
                }
            }
        }
        await Promise.all(pQueries);
        const evalResult = new EvalResult();
        evalResult.nTotalQueries = allQueries.length;
        for (const printResult of printResults) {
            if (printResult.correctDoc)
                evalResult.nDocsCorrect++;
            if (printResult.correctSentence)
                evalResult.nSentencesCorrect++;
        }
        printEvalResult(evalResult);
        console.log("Incorrect results: ");
        console.log(printResults.filter(r => !r.correctSentence || !r.correctDoc));
        return evalResult;
    }
}
class QueryResult {
    entryId;
    sentenceId;
    sentence;
    similarityValue;
}
class EvalResult {
    nDocsCorrect = 0;
    nSentencesCorrect = 0;
    nTotalQueries = 0;
}
function cosineSimilarity(v1, v2) {
    if (v1.length !== v2.length) {
        return -1;
    }
    let dotProduct = 0;
    let v1_mag = 0;
    let v2_mag = 0;
    for (let i = 0; i < v1.length; i++) {
        dotProduct += v1[i] * v2[i];
        v1_mag += v1[i] ** 2;
        v2_mag += v2[i] ** 2;
    }
    return dotProduct / (Math.sqrt(v1_mag) * Math.sqrt(v2_mag));
}
function printEvalResult(evalResult) {
    const nCorrectDocs = evalResult.nDocsCorrect;
    const nCorrectSentences = evalResult.nSentencesCorrect;
    const nTotal = evalResult.nTotalQueries;
    console.log(`${nCorrectDocs}/${nTotal} docs correct (${(100 * nCorrectDocs / nTotal).toFixed(1)}%)`);
    console.log(`${nCorrectSentences}/${nTotal} sentences correct (${(100 * nCorrectSentences / nTotal).toFixed(1)}%)`);
}
